import type { Address } from '@einwert/valuation-api-client'
import { Stack, styled } from '@mui/material'
import { ArrowRightIcon } from '@phosphor-icons/react'

import { assembleAssetTitle, Image, isNotNullOrUndefined, joinDefinedStrings, QuickViewBox, tokens } from 'ComponentLibrary'
import { useNavigate } from 'react-router-dom'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons'
import { TextWithEllipsis, Typography } from 'ComponentLibrary/src/piazza/text'
import { useTranslation } from 'react-i18next'

type AssetInfoBoxProps = {
    address: Address
    customerAssetReference?: string
    imageSrc?: string
    fallbackImageSrc?: string
    assetId?: string
}

export function AssetInfoBox({ imageSrc, fallbackImageSrc, customerAssetReference, assetId, address }: AssetInfoBoxProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.assetBox' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const navigate = useNavigate()
    const assetTitle = assembleAssetTitle({ ...address, customer_asset_reference: customerAssetReference })
    const addressLineTwo = joinDefinedStrings([address.zip, address.city])

    return (
        <QuickViewBox
            sx={{
                border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                borderRadius: tokens.SizingCornerM,
            }}
        >
            <Stack direction="row" height="100%" width="100%" alignItems="center">
                <StyledImage
                    src={imageSrc}
                    fallbackImageSrc={[fallbackImageSrc, '/asset-placeholder.svg'].filter(isNotNullOrUndefined)}
                    alt={t('imageAlt')}
                />
                <Stack
                    direction="column"
                    flexGrow={1}
                    minWidth={0}
                    height="100%"
                    padding={tokens.SizingSpacing200}
                    justifyContent="space-between"
                >
                    <Stack direction="row" width="100%" alignItems="center" justifyContent="space-between">
                        <Typography variant="headline2">{tTerminology('asset')}</Typography>
                        {assetId && (
                            <IconButton
                                size="small"
                                variant="tertiary"
                                tooltip={t('goToAsset')}
                                icon={<ArrowRightIcon />}
                                onClick={() => navigate(`/assets/${assetId}`)}
                            />
                        )}
                    </Stack>
                    <Stack direction="column">
                        <TextWithEllipsis tooltipText={assetTitle} typographyProps={{ variant: 'textS', color: tokens.ColorTextBold }}>
                            {assetTitle}
                        </TextWithEllipsis>
                        <TextWithEllipsis tooltipText={addressLineTwo} typographyProps={{ variant: 'textS', color: tokens.ColorTextBold }}>
                            {addressLineTwo}
                        </TextWithEllipsis>
                    </Stack>
                </Stack>
            </Stack>
        </QuickViewBox>
    )
}

const StyledImage = styled(Image)(() => ({
    width: '150px',
    height: '100%',
    borderRadius: `${tokens.SizingCornerM} 0 0 ${tokens.SizingCornerM}`,
    borderRight: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
}))
